import {
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@nexus/ui";
import { useMemo, useState, type ReactNode } from "react";

interface AdminOrdersMultiSelectProps<Item> {
  items: Item[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  getId: (item: Item) => string;
  getLabel: (item: Item) => string;
  renderItem: (item: Item) => ReactNode;
  renderSelectedItem?: (item: Item) => ReactNode;
  placeholder: string;
  selectedCountLabel: (count: number) => string;
  ariaLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
  clearLabel: string;
  className?: string;
  contentClassName?: string;
}

export function AdminOrdersMultiSelect<Item>({
  items,
  selectedIds,
  onChange,
  getId,
  getLabel,
  renderItem,
  renderSelectedItem,
  placeholder,
  selectedCountLabel,
  ariaLabel,
  searchPlaceholder,
  emptyMessage,
  clearLabel,
  className,
  contentClassName,
}: AdminOrdersMultiSelectProps<Item>) {
  const [open, setOpen] = useState(false);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedItems = useMemo(
    () => items.filter((item) => selectedIdSet.has(getId(item))),
    [getId, items, selectedIdSet],
  );

  const toggle = (id: string) => {
    onChange(
      selectedIdSet.has(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id],
    );
  };

  const triggerContent =
    selectedItems.length === 1 && renderSelectedItem
      ? renderSelectedItem(selectedItems[0]!)
      : selectedIds.length > 0
        ? selectedCountLabel(selectedIds.length)
        : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={cn(
            "flex h-8 min-w-44 items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-xs outline-none transition-colors hover:bg-muted/40 focus-visible:ring-1 focus-visible:ring-ring/40",
            selectedIds.length > 0 &&
              "border-primary/40 bg-primary-subtle/40 text-primary",
            className,
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              selectedIds.length === 0 && "text-muted-foreground",
            )}
          >
            {triggerContent}
          </span>
          <Icon
            name="chevrons-up-down"
            size="xs"
            color="inherit"
            aria-hidden
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className={cn("w-80 p-0", contentClassName)}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {items.map((item) => {
                const id = getId(item);
                const isSelected = selectedIdSet.has(id);
                return (
                  <CommandItem
                    key={id}
                    value={`${getLabel(item)} ${id}`}
                    aria-selected={isSelected}
                    className="min-h-11 gap-2"
                    onSelect={() => toggle(id)}
                  >
                    <Checkbox
                      checked={isSelected}
                      tabIndex={-1}
                      aria-hidden
                      className="pointer-events-none shrink-0"
                    />
                    {renderItem(item)}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          <div className="flex min-h-10 items-center justify-between gap-3 border-t border-border px-3 py-1.5">
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {selectedCountLabel(selectedIds.length)}
            </span>
            {selectedIds.length > 0 ? (
              <button
                type="button"
                className="text-xs font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/40"
                onClick={() => onChange([])}
              >
                {clearLabel}
              </button>
            ) : null}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
